import { FaSearch } from "react-icons/fa";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000KeyboardKeyHint from "../../../../u2000Core/components/common/U2000KeyboardKeyHint";
import U2000AutoCompletePanelResultsBlock, {
  U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS,
} from "../../../../u2000Core/components/common/U2000AutoCompletePanelResultsBlock";
import {
  createPanelCriteriaFocusHandler,
  U2000_AUTOCOMPLETE_PANEL_COMPACT,
} from "../../../../u2000Core/tools/u2000AutocompletePanelUtils";
import U2000SaveCancelDialog from "../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput, { U2000AutoCompleteInputResultTable } from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000InputTextBase from "../../../../u2000Core/components/formControls/input/U2000InputTextBase";
import type { U2104ImportAffiliationHandler } from "./U2104ImportAffiliationHandlers";
import { useState, useRef, useEffect, type Dispatch, type SetStateAction, type RefObject } from "react";

// Typage des props du composant
interface U2104ImportAffiliationDialogProps {
  u2104ImportAffiliationHandler: U2104ImportAffiliationHandler;

  t: (key: string, options?: any) => string;
  NS: string;
}

// Composant principal pour le dialog d'importation d'une affiliation
const U2104ImportAffiliationDialog = ({ u2104ImportAffiliationHandler, t, NS }: U2104ImportAffiliationDialogProps) => {
  // Ref for the autocomplete input to be used as default focus
  const autoCompleteRef = useRef<HTMLInputElement>(null);

  // (Using `requestFocus` prop on the autocomplete signals to request focus when dialog opens)
  // Extraction des données pour le champ username
  const extracDataForUsername = (result: any) => {
    if (result) {
      return [
        { fieldName: "user.id", value: result.id }, // Pour le formulaire
        { fieldName: "user.username", value: result.username },
        { fieldName: "user.firstName", value: result.firstName },
        { fieldName: "user.lastName", value: result.lastName },
        { fieldName: "user.email", value: result.email },
      ];
    } else {
      return [
        { fieldName: "user.id", value: null }, // Pour le formulaire
        { fieldName: "user.username", value: null },
        { fieldName: "user.firstName", value: null },
        { fieldName: "user.lastName", value: null },
        { fieldName: "user.email", value: null },
      ];
    }
  };

  // Fonction d'affichage pour l'autocomplete
  const displayFn = (item: any) => {
    return `${item.firstName} ${item.lastName} (${item.username})`;
  };

  // Composant pour le panneau de recherche avancée
  type CustomSearchPanelProps = {
    panelFields?: Record<string, any>;
    setPanelFields: Dispatch<SetStateAction<Record<string, any>>>;
    onPanelSearch: (fields: Record<string, any>) => void;
    results?: any[];
    totalCount?: number;
    truncated?: boolean;
    setShowPanel: Dispatch<SetStateAction<boolean>> | ((show: boolean) => void);
    handleRowClickFn?: (item: any, idx: number) => void;
    // Trigger boolean to notify panel that it was (re)opened so it can reset/focus fields
    resetPanelFieldsTrigger?: boolean;
  };

  // Panneau de recherche avancée personnalisé
  const CustomSearchPanel = ({ panelFields = {}, setPanelFields, onPanelSearch, results = [], totalCount, truncated, setShowPanel, handleRowClickFn, resetPanelFieldsTrigger }: CustomSearchPanelProps) => {
    // Déclenche la recherche avancée sur Enter et mémorise le champ courant

    const [showResults, setShowResults] = useState<boolean>(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

    // Met à jour l'affichage des résultats lorsque les résultats changent
    useEffect(() => {
      if (results && results.length > 0) {
        setShowResults(true);
        setHighlightedIndex(-1);
      } else {
        setShowResults(false);
        setHighlightedIndex(-1);
      }
    }, [results]);

    const handleCriteriaFocus = createPanelCriteriaFocusHandler(setHighlightedIndex);

    // Gestion du Escape pour fermer le panel
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setShowPanel(false);
          setShowResults(false);
        }
        if (e.key === "F3") {
          e.preventDefault();
          onPanelSearch(panelFields);
        }
      };
      window.addEventListener("keydown", handleKeyDown as any);
      return () => window.removeEventListener("keydown", handleKeyDown as any);
    }, [panelFields, onPanelSearch, setShowPanel, setShowResults]);

    // Ref to the panel DOM node used for trapping focus
    const panelRef = useRef<HTMLDivElement | null>(null);
    // Ref to the firstName input so we can focus it when the panel opens
    const firstNameRef = useRef<HTMLInputElement | null>(null);

    // Helper: get focusable elements inside the panel
    const getFocusableElements = () => {
      if (!panelRef.current) return [] as HTMLElement[];
      const selectors = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", '[tabindex]:not([tabindex="-1"])'];
      const nodeList = panelRef.current.querySelectorAll(selectors.join(","));
      return Array.prototype.slice.call(nodeList) as HTMLElement[];
    };

    // Trap Tab / Shift+Tab inside the panel so focus doesn't escape
    const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Tab") return;
      const focusable = getFocusableElements().filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // If nothing inside has focus, move to first
      if (!panelRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    };

    // When panel mounts, ensure focus is inside.
    // Strategy:
    // 1) Focus the panel container so the dialog's focus trap sees focus is already inside.
    // 2) On the next animation frame (after paint) focus the firstName input.
    // This ordering prevents the dialog-level focus handling from stealing focus back.
    useEffect(() => {
      if (!panelRef.current) return;

      // Ensure the panel container receives focus first (so the dialog trap won't move focus away)
      try {
        panelRef.current.focus();
      } catch (err) {
        // ignore
      }

      // Wait for next frame/paint then focus the input if present
      requestAnimationFrame(() => {
        if (firstNameRef.current) {
          try {
            firstNameRef.current.focus();
          } catch (err) {
            // ignore
          }
          return;
        }

        // Fallback: focus first focusable element inside the panel
        const focusable = getFocusableElements();
        if (focusable.length > 0 && !panelRef.current?.contains(document.activeElement)) {
          try {
            focusable[0].focus();
          } catch (err) {
            // ignore
          }
        }
      });
      // no cleanup required
    }, []);

    // When parent toggles the reset trigger (dialog opened), focus the firstName input
    useEffect(() => {
      if (typeof resetPanelFieldsTrigger !== "undefined") {
        if (resetPanelFieldsTrigger && panelRef.current) {
          try {
            // Focus container first then input on next frame
            panelRef.current.focus();
          } catch (err) {
            // ignore
          }
          requestAnimationFrame(() => {
            if (firstNameRef.current) {
              try {
                firstNameRef.current.focus();
              } catch (err) {
                // ignore
              }
            }
          });
        }
      }
    }, [resetPanelFieldsTrigger]);

    function handleRowClick(item: any, idx: number) {
      setShowPanel(false); // Ferme le panel après la sélection
      setHighlightedIndex(-1);
      if (handleRowClickFn) {
        handleRowClickFn(item, idx);
      }
    }

    return (
      <div
        ref={panelRef}
        onKeyDown={handleTrapKeyDown}
        className={`flex h-full flex-col ${U2000_AUTOCOMPLETE_PANEL_COMPACT.rootGap} rounded-lg border border-gray-200 bg-white p-4 shadow-lg animate-fade-in`}
        tabIndex={0}
      >
        <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaToolbar}>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaFields} onFocusCapture={handleCriteriaFocus}>
            <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.criteriaRow}>
              <div className="flex-1">
                <U2000InputTextBase
                  placeholder={t("user.lblFirstName", { ns: NS })}
                  fieldName="firstName"
                  type={"string"}
                  value={panelFields.firstName || null}
                  setValue={(value) => setPanelFields((fields) => ({ ...fields, firstName: value }))}
                  inputRef={firstNameRef}
                />
              </div>
              <div className="flex-1">
                <U2000InputTextBase
                  placeholder={t("user.lblLastName", { ns: NS })}
                  fieldName="lastName"
                  type={"string"}
                  value={panelFields.lastName || null}
                  setValue={(value) => setPanelFields((fields) => ({ ...fields, lastName: value }))}
                />
              </div>
            </div>
            <U2000InputTextBase
              placeholder={t("user.lblEmail", { ns: NS })}
              fieldName="email"
              type={"string"}
              value={panelFields.email || null}
              setValue={(value) => setPanelFields((fields) => ({ ...fields, email: value }))}
            />
          </div>
          <div className={U2000_AUTOCOMPLETE_PANEL_COMPACT.searchRow}>
            <U2000ActionBtn
              className="w-full"
              label={t("btnSearch")}
              icon={FaSearch}
              trailingHint={<U2000KeyboardKeyHint>F3</U2000KeyboardKeyHint>}
              handleClick={() => onPanelSearch(panelFields)}
            />
          </div>
        </div>

        <U2000AutoCompletePanelResultsBlock highlightedIndex={highlightedIndex}>
          <U2000AutoCompleteInputResultTable
            results={results}
            resultLabels={[
              t("import.lblImportUserName", { ns: NS }),
              t("import.lblImportUserFirstName", { ns: NS }),
              t("import.lblImportUserLastName", { ns: NS }),
              t("import.lblImportUserEmail", { ns: NS }),
            ]}
            resultFields={["username", "firstName", "lastName", "email"]}
            handleRowClick={handleRowClick}
            showResults={showResults}
            setShowResults={setShowResults}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            className={U2000_AUTOCOMPLETE_PANEL_RESULT_TABLE_CLASS}
            totalCount={totalCount}
            truncated={truncated}
          />
        </U2000AutoCompletePanelResultsBlock>
      </div>
    );
  };

  // Render
  return (
    <U2000SaveCancelDialog
      width="w-[700px]"
      isOpen={u2104ImportAffiliationHandler.isDialogOpen}
      onClose={u2104ImportAffiliationHandler.onDlgClose}
      title={t("lblImportUser", { ns: NS })}
      defaultFocusRef={autoCompleteRef as RefObject<HTMLElement>}
    >
      <div className="flex flex-col gap-y-4">
        <U2000AutoCompleteInput
          inputRef={autoCompleteRef as React.RefObject<HTMLInputElement>}
          pageSize={50}
          formStateHandlers={u2104ImportAffiliationHandler.formStateHandlers}
          fieldName="user"
          mainFieldName="username"
          label={t("import.lblImportUserName", { ns: NS })}
          onSearch={u2104ImportAffiliationHandler.onSearch}
          displayFn={displayFn}
          overrideExtractDataFn={extracDataForUsername}
          basicResultHeight={300}
          customSearchPanelHeight={500}
          resultLabels={[
            t("import.lblImportUserName", { ns: NS }),
            t("import.lblImportUserFirstName", { ns: NS }),
            t("import.lblImportUserLastName", { ns: NS }),
            t("import.lblImportUserEmail", { ns: NS }),
          ]}
          resultFields={["username", "firstName", "lastName", "email"]}
          requestFocus={u2104ImportAffiliationHandler.isDialogOpen}
          renderPanel={(props) => <CustomSearchPanel {...props} resetPanelFieldsTrigger={u2104ImportAffiliationHandler.isDialogOpen} />}
        />
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U2104ImportAffiliationDialog;
